import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../lib/api';
import { r16pUiEnabled } from '../lib/r14Switch';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem('accessToken'));

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    setAccessToken(token);
    if (!token) {
      setIsLoading(false);
      setIsAuthenticated(false);
      return;
    }

    try {
      const response = await authApi.getMe();
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setAccessToken(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await authApi.login(email, password);
    const { access_token, refresh_token } = response.data;
    
    localStorage.setItem('accessToken', access_token);
    localStorage.setItem('refreshToken', refresh_token);
    setAccessToken(access_token);
    
    // Get user info
    const userResponse = await authApi.getMe();
    setUser(userResponse.data);
    setIsAuthenticated(true);
    
    return response.data;
  };

  /*
   * r16pm review (2026-10-25): "Log out" used to clear only this tab's state - the SERVER session stayed alive, so on a shared PC the
   * next person could still open the account's pages (and, with r16p, its DM text) by URL with the same cookie. Now the local state
   * is cleared at once (the sign-in page shows without waiting) and POST /uiapi/auth/logout ends the server session (it invalidates
   * the HttpSession). Best effort: a failed call (server down, session already gone) is logged and the local sign-out stands. Never a
   * /login bounce from the call itself (necroNoAuthRedirect). Resolves true when the server confirmed.
   * Rollback: the r16p switch (localStorage necro.ui.r16p=off / REACT_APP_NECRO_UI_R16P=off) = the r16n local-only sign-out.
   */
  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setAccessToken(null);
    setUser(null);
    setIsAuthenticated(false);
    if (!r16pUiEnabled()) return Promise.resolve(false);
    return Promise.resolve()
      .then(() => authApi.logout())
      .then(() => {
        // eslint-disable-next-line no-console
        console.info('[R16P-UI] sign-out: the server session ended');
        return true;
      }, (e) => {
        const status = e && e.response && typeof e.response.status === 'number' ? `HTTP ${e.response.status}` : 'no answer';
        // eslint-disable-next-line no-console
        console.info(`[R16P-UI] sign-out: the server did not confirm (${status}); signed out in this tab`);
        return false;
      });
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    checkAuth,
    accessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/** r16m: the auth context, or null outside an AuthProvider (a page that only reads who is signed in, and must not throw in tests). */
export function useOptionalAuth() {
  return useContext(AuthContext);
}
